import { Fragment, useEffect, useMemo, useState } from "react";
import {
  ApiClientError,
  fetchMallAdsBid,
  saveMallAdsBidConfig,
  saveMallAdsBidManual,
  saveMallAdsBidProbe,
  runMallAdsBidNow,
  saveMallAdsBidRule,
} from "../../api/client";
import { StateBanner } from "../../components/StateBanner";
import { formatHistoryAt } from "../../lib/mkRules";
import type {
  MallAdsBidResponse,
  RppBidAction,
  RppBidBounds,
  RppBidConfig,
  RppBidGuide,
  RppBidKeywordRow,
  RppBidLastRun,
  RppBidLogRow,
  RppBidManual,
  RppBidPerf,
  RppBidProbe,
  RppBidRule,
} from "../../types/contract";
import "../../styles/mall-ads.css";

/**
 * 楽天RPPのキーワードCPC下限（円）。RMSはこれ未満を一括登録で弾くため、ルールの min_cpc 既定値はこれに揃える
 * （worker: rpp-bid-engine.ts RPP_MIN_CPC / validateRuleInput と同値。2026-10-04 旧既定10円→40円）。
 */
const RPP_MIN_CPC = 40;
/** 手動CPCの上限（worker契約 plan v2 §3-4: 40..10000）。 */
const RPP_MAX_MANUAL_CPC = 10000;
/** rank_log / 順位CSV の取得間隔（時間）。探索の所要時間・次回runの目安に使う。 */
const RUN_INTERVAL_HOURS = 3;
/** テーブル列数（キーワード/順位/現CPC/目安/実績CPC/目標/上限CPC/前回/状態）。見出し行・詳細パネルの colSpan。 */
const TABLE_COLS = 9;

/**
 * 順位自動入札セクション（tmp/fable/2026-09-25-rpp-bid/plan.md §6 / v2: tmp/fable/2026-10-05-rpp-bid-v2/plan.md §5）。
 * MallAdsPage本体（RPP実績スナップショット）とは独立に、自前でGET /mall-ads/bidを読み込む
 * （snapshotがnullでも常に表示する）。
 *
 * 編集の対象は常にトップレベルの `rules`（キー: manage_number+keyword）。`keywords[].rule` は
 * その時点でエンジンが実際に適用した内容の記録（過去の観測値）であり、編集フォームの初期値には
 * 使わない（現在の rules が正）。
 */

type FetchState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ok"; data: MallAdsBidResponse };

interface ActionCtx {
  cpc: number | null;
  new_cpc: number | null;
}

/** 行の cpc→new_cpc の実差分（`+45円`／`−30円`）。差分が出せない時は fallback（名前）を返す。 */
function diffOr(ctx: ActionCtx, fallback: string, prefix = ""): string {
  if (ctx.cpc !== null && ctx.new_cpc !== null && ctx.new_cpc !== ctx.cpc) {
    const d = ctx.new_cpc - ctx.cpc;
    return `${prefix}${d > 0 ? `+${d}円` : `−${Math.abs(d)}円`}`;
  }
  return fallback;
}

const ACTION_LABEL: Record<RppBidAction, (ctx: ActionCtx) => string> = {
  up: (c) => diffOr(c, "上げ"),
  down: (c) => diffOr(c, "下げ"),
  revert: (c) => diffOr(c, "戻し"),
  // 手動・探索は上げ/下げと区別が付くよう、差分の前に名前を添える。
  manual: (c) => diffOr(c, "手動", "手動 "),
  probe: (c) => diffOr(c, "探索", "探索 "),
  probe_end: (c) => diffOr(c, "探索終了", "探索終了 "),
  hold_max: () => "上限",
  hold_floor: () => "床",
  hold_min: () => "下限",
  hold_fail: () => "負け値",
  hold_lock: () => "ロック",
  hold_probe: () => "探索待ち",
  hold_missing: () => "表示なし",
  hold_stale: () => "変化なし",
  skip: () => "対象外",
};

const ACTION_CLASS: Record<RppBidAction, string> = {
  up: "bid-action-up",
  down: "bid-action-down",
  revert: "bid-action-revert",
  manual: "bid-action-manual",
  probe: "bid-action-probe",
  probe_end: "bid-action-probe",
  hold_max: "bid-action-hold",
  hold_floor: "bid-action-hold",
  hold_min: "bid-action-hold",
  hold_fail: "bid-action-hold",
  hold_lock: "bid-action-hold",
  hold_probe: "bid-action-hold",
  hold_missing: "bid-action-hold",
  hold_stale: "bid-action-hold",
  skip: "bid-action-skip",
};

const RUN_STATUS_LABEL: Record<string, string> = {
  applied: "適用済み",
  decided: "判定のみ",
  failed: "失敗",
  dry_run: "DRY RUN",
};

const LOCK_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "反映のみ" },
  { value: "24", label: "24時間" },
  { value: "72", label: "72時間" },
  { value: "168", label: "7日" },
  { value: "-1", label: "固定" },
];

function ruleKey(manageNumber: string, keyword: string): string {
  return `${manageNumber}\u0000${keyword}`;
}

/** サーバー日時（'YYYY-MM-DD HH:MM:SS' UTC・Zなし、または ISO）を epoch ms に。 */
function parseAtMs(at: string): number {
  const iso = /T|Z$/.test(at) ? at : `${at.replace(" ", "T")}Z`;
  return new Date(iso).getTime();
}

function lockLabel(lockHours: number | null): string {
  if (lockHours === null) return "反映のみ";
  if (lockHours === -1) return "固定";
  if (lockHours === 168) return "7日";
  return `${lockHours}時間`;
}

function isFixedLock(m: RppBidManual): boolean {
  return m.lock_hours === -1 || (m.lock_until?.startsWith("9999") ?? false);
}

/** 次回run（3時間ごと・hh:10）の目安。最終実行時刻から3時間刻みで未来へ進める（実スケジュール未確認＝目安）。 */
function nextRunLabel(lastRun: RppBidLastRun | null): string | null {
  if (!lastRun) return null;
  const base = parseAtMs(lastRun.checked_at);
  if (Number.isNaN(base)) return null;
  const step = RUN_INTERVAL_HOURS * 3600_000;
  const now = Date.now();
  let next = base + step;
  if (next <= now) next = base + Math.ceil((now - base) / step) * step;
  const d = new Date(next);
  d.setUTCMinutes(10, 0, 0); // JSTは整時間オフセットなので分はUTCと同じ
  return formatHistoryAt(d.toISOString());
}

function ActionBadge({ action, cpc, newCpc }: { action: RppBidAction | null; cpc: number | null; newCpc: number | null }) {
  if (!action) return <span className="bid-rank-missing">—</span>;
  return (
    <span className={`bid-action-label ${ACTION_CLASS[action]}`}>{ACTION_LABEL[action]({ cpc, new_cpc: newCpc })}</span>
  );
}

function renderRank(row: RppBidKeywordRow): JSX.Element {
  if (row.rank !== null) return <>{row.rank}位</>;
  if (row.last_shown_at) {
    return <span className="bid-rank-missing">表示なし（前回 {formatHistoryAt(row.last_shown_at)}）</span>;
  }
  return <span className="bid-rank-missing">表示なし</span>;
}

/** 目安: `¥64` ＋ 小さく `40–190`。取得できていない時は —。 */
function GuideCell({ guide }: { guide: RppBidGuide | null }) {
  if (!guide || (guide.cpc === null && guide.min === null && guide.max === null)) return <>—</>;
  return (
    <span className="bid-cell-stack" title={guide.at ? `RMS目安（${formatHistoryAt(guide.at)} 取得）` : "RMS目安"}>
      <span>{guide.cpc !== null ? `¥${guide.cpc}` : "—"}</span>
      {guide.min !== null && guide.max !== null && (
        <span className="bid-cell-sub">
          {guide.min}–{guide.max}
        </span>
      )}
    </span>
  );
}

/** 'YYYY-MM-DD' → 'M/D'。 */
function shortDate(ymd: string): string {
  const m = /^\d{4}-(\d{2})-(\d{2})$/.exec(ymd);
  return m ? `${Number(m[1])}/${Number(m[2])}` : ymd;
}

/**
 * 実績CPC（RPP効果レポート）: 主表示は直近日の実効CPC（直近2スナップショットの差分 spend/clicks）、
 * 下に月累計（月初〜前日）を小さく。直近日が無い（初回取込・月替わり直後）ときは月累計のみ。
 */
function PerfCell({ perf }: { perf: RppBidPerf | null }) {
  if (!perf) return <>—</>;
  const recent = perf.recent ?? null;
  const recentRange = recent ? (recent.from === recent.to ? shortDate(recent.to) : `${shortDate(recent.from)}–${shortDate(recent.to)}`) : null;
  const monthLabel = `月累計 ¥${perf.cpc}（${perf.clicks}クリック）`;
  const title = [
    recent ? `直近日 ${recentRange}: 費用¥${recent.spend.toLocaleString()} / ${recent.clicks}クリック` : "直近日: 差分なし（初回取込または月替わり直後）",
    `月初〜${perf.snapshot_date}: 費用¥${perf.spend.toLocaleString()} / ${perf.clicks}クリック / ROAS ${perf.roas}`,
  ].join("\n");
  if (!recent) {
    return (
      <span className="bid-cell-stack" title={title}>
        <span>¥{perf.cpc}</span>
        <span className="bid-cell-sub">月累計・{perf.clicks}クリック</span>
      </span>
    );
  }
  return (
    <span className="bid-cell-stack" title={title}>
      <span>{recent.cpc !== null ? `¥${recent.cpc}` : "—"}</span>
      <span className="bid-cell-sub">
        {recentRange}・{recent.clicks}クリック
      </span>
      <span className="bid-cell-sub">{monthLabel}</span>
    </span>
  );
}

/** 状態セル: 床／ロック中／手動反映待ち／探索中 を短いバッジで。 */
function StatusCell({ row }: { row: RppBidKeywordRow }) {
  const floorActive =
    !!row.state?.floor_until && new Date(`${row.state.floor_until.replace(" ", "T")}Z`).getTime() > Date.now();
  const manual = row.manual;
  const lockActive = row.lock_active && !!manual;
  const pending = !!manual && manual.applied_at === null;
  const probeRunning = row.probe?.status === "running";
  const badges: JSX.Element[] = [];

  if (floorActive && row.state?.floor_cpc !== null && row.state?.floor_until) {
    badges.push(
      <span className="bid-floor-note" key="floor">
        床 ¥{row.state.floor_cpc}（〜{formatHistoryAt(row.state.floor_until)}）
      </span>
    );
  }
  if (lockActive && manual) {
    badges.push(
      <span className="bid-badge bid-badge-lock" key="lock">
        {isFixedLock(manual)
          ? "ロック中 固定"
          : manual.lock_until
            ? `ロック中 〜${formatHistoryAt(manual.lock_until)}`
            : "ロック中"}
      </span>
    );
  }
  if (pending && manual) {
    badges.push(
      <span className="bid-badge bid-badge-pending" key="pending" title="次回run（最長3時間後）に反映されます">
        次回反映 ¥{manual.cpc}
      </span>
    );
  }
  if (probeRunning && row.probe) {
    badges.push(
      <span className="bid-badge bid-badge-probe" key="probe">
        探索中 {row.probe.runs}/{row.probe.max_runs}
      </span>
    );
  }
  if (badges.length === 0) return <>—</>;
  return <span className="bid-status-stack">{badges}</span>;
}

function manualSummary(m: RppBidManual): string {
  const lock = m.lock_hours === null ? "ロックなし" : `ロック ${lockLabel(m.lock_hours)}`;
  if (m.applied_at === null) {
    return `要求中 ¥${m.cpc}（${lock}）・${formatHistoryAt(m.requested_at)} 要求・次回run待ち`;
  }
  const lockState = isFixedLock(m)
    ? "固定中"
    : m.lock_until
      ? parseAtMs(m.lock_until) > Date.now()
        ? `ロック 〜${formatHistoryAt(m.lock_until)}`
        : "ロック終了"
      : "ロックなし";
  return `反映済み ¥${m.cpc}（${formatHistoryAt(m.applied_at)}）・${lockState}`;
}

/** 探索の開始CPC表示。null（古いworkerで未定義も含む）は現在値から。 */
function probeStartText(probe: RppBidProbe): string {
  return probe.start_cpc != null ? `開始 ¥${probe.start_cpc}（目安上限+上乗せ）` : "現在値から";
}

/** 探索の終了条件。1位狙いは1位を外した時点、3・5位狙いは until_rank（既定5）位より下。 */
function probeEndText(probe: RppBidProbe, targetRank: 1 | 3 | 5 | null): string {
  return targetRank === 1 || probe.until_rank <= 1 ? "1位を外したら終了" : `${probe.until_rank}位より下で終了`;
}

function boundsText(b: RppBidBounds | null): JSX.Element {
  if (!b || (b.good_cpc === null && b.fail_cpc === null)) return <>—</>;
  return (
    <>
      <span>
        目標内 {b.good_cpc !== null ? `¥${b.good_cpc}` : "—"}
        {b.good_at ? <span className="bid-cell-sub">（{formatHistoryAt(b.good_at)}）</span> : null}
      </span>
      <span className="bid-sep">・</span>
      <span>
        目標外 {b.fail_cpc !== null ? `¥${b.fail_cpc}` : "—"}
        {b.fail_at ? <span className="bid-cell-sub">（{formatHistoryAt(b.fail_at)}）</span> : null}
      </span>
    </>
  );
}

export function BidControlSection() {
  const [state, setState] = useState<FetchState>({ status: "loading" });
  const [showLog, setShowLog] = useState(false);
  const [newManageNumber, setNewManageNumber] = useState("");
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());

  const load = () => {
    setState({ status: "loading" });
    fetchMallAdsBid()
      .then((data) => setState({ status: "ok", data }))
      .catch((e: unknown) => {
        const message = e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。";
        setState({ status: "error", message });
      });
  };

  useEffect(load, []);

  if (state.status === "loading") {
    return (
      <div className="card bid-section">
        <div className="skeleton" style={{ height: 16, width: "40%", marginBottom: 10 }} />
        <div className="skeleton" style={{ height: 12, width: "70%" }} />
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div className="card bid-section">
        <StateBanner kind="error" message={state.message} onRetry={load} />
      </div>
    );
  }

  return (
    <BidBody
      data={state.data}
      setData={(data) => setState({ status: "ok", data })}
      showLog={showLog}
      setShowLog={setShowLog}
      newManageNumber={newManageNumber}
      setNewManageNumber={setNewManageNumber}
      savingKeys={savingKeys}
      setSavingKeys={setSavingKeys}
    />
  );
}

function BidBody({
  data,
  setData,
  showLog,
  setShowLog,
  newManageNumber,
  setNewManageNumber,
  savingKeys,
  setSavingKeys,
}: {
  data: MallAdsBidResponse;
  setData: (data: MallAdsBidResponse) => void;
  showLog: boolean;
  setShowLog: (v: boolean) => void;
  newManageNumber: string;
  setNewManageNumber: (v: string) => void;
  savingKeys: Set<string>;
  setSavingKeys: (v: Set<string>) => void;
}) {
  const { config, rules, keywords, last_run, recent_log } = data;
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // 「今すぐ実行」「再読込」の状態。通知は alert ではなくヘッダ直下にテキスト表示する。
  const [runBusy, setRunBusy] = useState(false);
  const [reloadBusy, setReloadBusy] = useState(false);
  const [runNote, setRunNote] = useState<{ kind: "ok" | "error"; text: string; reload: boolean } | null>(null);

  const rulesByKey = useMemo(() => {
    const map = new Map<string, RppBidRule>();
    for (const r of rules) map.set(ruleKey(r.manage_number, r.keyword), r);
    return map;
  }, [rules]);

  const manageNumbers = useMemo(() => {
    const set = new Set<string>();
    for (const r of rules) set.add(r.manage_number);
    for (const k of keywords) set.add(k.manage_number);
    return Array.from(set).sort();
  }, [rules, keywords]);

  const keywordsByManage = useMemo(() => {
    const map = new Map<string, RppBidKeywordRow[]>();
    for (const k of keywords) {
      const list = map.get(k.manage_number) ?? [];
      list.push(k);
      map.set(k.manage_number, list);
    }
    return map;
  }, [keywords]);

  const nextRun = useMemo(() => nextRunLabel(last_run), [last_run]);

  const withSaving = async <T,>(key: string, fn: () => Promise<T>): Promise<T | null> => {
    const next = new Set(savingKeys);
    next.add(key);
    setSavingKeys(next);
    try {
      return await fn();
    } catch (e) {
      window.alert(e instanceof ApiClientError ? e.message : "保存に失敗しました。");
      return null;
    } finally {
      const after = new Set(savingKeys);
      after.delete(key);
      setSavingKeys(after);
    }
  };

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const upsertRule = (rule: RppBidRule) => {
    const next = rules.filter((r) => !(r.manage_number === rule.manage_number && r.keyword === rule.keyword));
    next.push(rule);
    setData({ ...data, rules: next });
  };

  const removeRule = (manageNumber: string, keyword: string) => {
    setData({
      ...data,
      rules: rules.filter((r) => !(r.manage_number === manageNumber && r.keyword === keyword)),
    });
  };

  const handleToggleEnabled = async (checked: boolean) => {
    if (checked) {
      const ok = window.confirm("3時間ごとにキーワードCPCを自動変更します。開始しますか？");
      if (!ok) return;
    }
    const key = "config:enabled";
    const res = await withSaving(key, () => saveMallAdsBidConfig("enabled", checked ? "1" : "0"));
    if (res) setData({ ...data, config: res.config });
  };

  const handleConfigNumberBlur = async (key: keyof RppBidConfig, raw: string) => {
    if (raw.trim() === "") return;
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    if (n === Number(config[key])) return; // 変更なし
    const res = await withSaving(`config:${key}`, () => saveMallAdsBidConfig(key, String(n)));
    if (res) setData({ ...data, config: res.config });
  };

  const handleConfigBooleanChange = async (key: keyof RppBidConfig, checked: boolean) => {
    const res = await withSaving(`config:${key}`, () => saveMallAdsBidConfig(key, checked ? "1" : "0"));
    if (res) setData({ ...data, config: res.config });
  };

  const handleTargetSelect = async (
    manageNumber: string,
    keyword: string,
    value: string,
    existing: RppBidRule | undefined,
    fallback: RppBidRule | undefined
  ) => {
    const key = ruleKey(manageNumber, keyword);
    if (value === "") {
      // 商品既定に戻す＝キーワード側ルールを削除（商品既定行では選択不可なのでkeyword行のみ通る）。
      if (!existing) return;
      const res = await withSaving(key, () =>
        saveMallAdsBidRule({ manage_number: manageNumber, keyword, delete: true })
      );
      if (res) removeRule(manageNumber, keyword);
      return;
    }
    if (value === "off") {
      const res = await withSaving(key, () =>
        saveMallAdsBidRule({
          manage_number: manageNumber,
          keyword,
          target_rank: existing?.target_rank ?? fallback?.target_rank ?? 3,
          max_cpc: existing?.max_cpc ?? fallback?.max_cpc ?? 150,
          min_cpc: existing?.min_cpc ?? fallback?.min_cpc ?? RPP_MIN_CPC,
          enabled: false,
        })
      );
      if (res?.rule) upsertRule(res.rule);
      return;
    }
    const target_rank = Number(value) as 1 | 3 | 5;
    const res = await withSaving(key, () =>
      saveMallAdsBidRule({
        manage_number: manageNumber,
        keyword,
        target_rank,
        max_cpc: existing?.max_cpc ?? fallback?.max_cpc ?? 150,
        min_cpc: existing?.min_cpc ?? fallback?.min_cpc ?? RPP_MIN_CPC,
        enabled: true,
      })
    );
    if (res?.rule) upsertRule(res.rule);
  };

  const handleMaxCpcBlur = async (
    manageNumber: string,
    keyword: string,
    raw: string,
    existing: RppBidRule | undefined,
    fallback: RppBidRule | undefined
  ) => {
    if (raw.trim() === "") return; // 空欄=商品既定に従う（現状維持。明示的な削除は目標「商品既定」選択で行う）
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return;
    const key = ruleKey(manageNumber, keyword);
    const res = await withSaving(key, () =>
      saveMallAdsBidRule({
        manage_number: manageNumber,
        keyword,
        target_rank: existing?.target_rank ?? fallback?.target_rank ?? 3,
        max_cpc: n,
        min_cpc: existing?.min_cpc ?? fallback?.min_cpc ?? RPP_MIN_CPC,
        enabled: existing?.enabled ?? true,
      })
    );
    if (res?.rule) upsertRule(res.rule);
  };

  const handleAddProduct = async () => {
    const mn = newManageNumber.trim();
    if (!mn) return;
    const key = ruleKey(mn, "");
    const res = await withSaving(key, () =>
      saveMallAdsBidRule({ manage_number: mn, keyword: "", target_rank: 3, max_cpc: 150, min_cpc: RPP_MIN_CPC, enabled: true })
    );
    if (res?.rule) {
      upsertRule(res.rule);
      setNewManageNumber("");
    }
  };

  // ---- v2: 手動CPC・探索。POST後は GET /mall-ads/bid を取り直して全体を差し替える ----

  const handleManualApply = async (row: RppBidKeywordRow, cpc: number, lockHours: number | null) => {
    const fresh = await withSaving(`manual:${ruleKey(row.manage_number, row.keyword)}`, async () => {
      await saveMallAdsBidManual({ manage_number: row.manage_number, keyword: row.keyword, cpc, lock_hours: lockHours });
      return fetchMallAdsBid();
    });
    if (fresh) setData(fresh);
  };

  const handleManualCancel = async (row: RppBidKeywordRow) => {
    const fresh = await withSaving(`manual:${ruleKey(row.manage_number, row.keyword)}`, async () => {
      await saveMallAdsBidManual({ manage_number: row.manage_number, keyword: row.keyword, cancel: true });
      return fetchMallAdsBid();
    });
    if (fresh) setData(fresh);
  };

  /** 探索開始の確認文言。目標1位は目安範囲の上限＋上乗せから探り始め、1位を外したら終了する。 */
  const probeConfirmMessage = (row: RppBidKeywordRow): string => {
    const n = config.probe_max_runs;
    const rule = rulesByKey.get(ruleKey(row.manage_number, row.keyword)) ?? rulesByKey.get(ruleKey(row.manage_number, ""));
    if (rule && rule.enabled && rule.target_rank === 1) {
      const guideMax = row.guide?.max ?? null;
      if (guideMax !== null) {
        const anchor = guideMax + config.guide_margin;
        const start = row.cpc !== null ? Math.min(row.cpc, anchor) : anchor;
        return (
          `1位狙い: 目安範囲の上限 ¥${guideMax}＋${config.guide_margin}円（=¥${start}）から探り始め、` +
          `1位を外した時点で終了します（最大 ${n} 回・${config.probe_step_pct}% 刻み）。` +
          `終了後は見つかった最安の1位CPC（無ければ元の値）に戻します。`
        );
      }
      return (
        `1位狙い（目安なし）: 現在値から ${config.probe_step_pct}% 刻みで下げ、1位を外した時点で終了します（最大 ${n} 回）。` +
        `終了後は見つかった最安の1位CPC（無ければ元の値）に戻します。`
      );
    }
    return (
      `最大 ${n} 回（約 ${n * RUN_INTERVAL_HOURS} 時間）、意図的にCPCを下げて順位を落とします。` +
      `終了後は見つかった最安の目標内CPC（無ければ元の値）に戻します。`
    );
  };

  const handleProbeStart = async (row: RppBidKeywordRow) => {
    const ok = window.confirm(probeConfirmMessage(row));
    if (!ok) return;
    const fresh = await withSaving(`probe:${ruleKey(row.manage_number, row.keyword)}`, async () => {
      await saveMallAdsBidProbe({ manage_number: row.manage_number, keyword: row.keyword, action: "start" });
      return fetchMallAdsBid();
    });
    if (fresh) setData(fresh);
  };

  const handleProbeCancel = async (row: RppBidKeywordRow) => {
    const restore = row.probe?.restore_cpc;
    const ok = window.confirm(
      restore != null
        ? `探索を中止します。次回runで元のCPC（¥${restore}）に戻します。よろしいですか？`
        : "探索を中止します。よろしいですか？"
    );
    if (!ok) return;
    const fresh = await withSaving(`probe:${ruleKey(row.manage_number, row.keyword)}`, async () => {
      await saveMallAdsBidProbe({ manage_number: row.manage_number, keyword: row.keyword, action: "cancel" });
      return fetchMallAdsBid();
    });
    if (fresh) setData(fresh);
  };

  // ---- 今すぐ実行（Cloud Run Job を即時起動。config.enabled が OFF でも押せる） ----

  const handleRunNow = async () => {
    const ok = window.confirm(
      "RPP入札ジョブを今すぐ起動します（約3分で反映）。手動CPCの反映待ち・探索の開始を待たずに適用したいときに使います。"
    );
    if (!ok) return;
    setRunBusy(true);
    setRunNote(null);
    try {
      const res = await runMallAdsBidNow();
      const tail = res.execution ? res.execution.split("/").pop() : null;
      setRunNote({
        kind: "ok",
        text: `起動しました${tail ? `（${tail}）` : ""}。約3分後に再読込してください`,
        reload: true,
      });
    } catch (e) {
      setRunNote({
        kind: "error",
        text: e instanceof ApiClientError ? e.message : "起動に失敗しました。",
        reload: false,
      });
    } finally {
      setRunBusy(false);
    }
  };

  const handleReload = async () => {
    setReloadBusy(true);
    try {
      setData(await fetchMallAdsBid());
      setRunNote(null);
    } catch (e) {
      setRunNote({
        kind: "error",
        text: e instanceof ApiClientError ? e.message : "再読込に失敗しました。",
        reload: true,
      });
    } finally {
      setReloadBusy(false);
    }
  };

  const isEmpty = keywords.length === 0;

  return (
    <div className="card bid-section">
      <div className="bid-header-row">
        <h2 className="bid-title">順位自動入札</h2>
        <div className="bid-toggle-row">
          <button
            type="button"
            className="btn btn-sm"
            disabled={runBusy}
            onClick={handleRunNow}
            title="RPP入札ジョブ（3時間ごと）を今すぐ起動します。自動入札がOFFでも判定だけ記録されます"
          >
            {runBusy ? "起動中…" : "今すぐ実行"}
          </button>
          <span className="bid-toggle-label">{config.enabled ? "稼働中" : "停止中"}</span>
          <label className="bid-switch">
            <input
              type="checkbox"
              checked={config.enabled}
              disabled={savingKeys.has("config:enabled")}
              onChange={(e) => handleToggleEnabled(e.target.checked)}
            />
            <span className="slider" />
          </label>
        </div>
      </div>

      {runNote && (
        <div className={`bid-runnote bid-runnote-${runNote.kind}`} role={runNote.kind === "error" ? "alert" : "status"}>
          <span>{runNote.text}</span>
          {runNote.reload && (
            <button type="button" className="btn btn-sm" disabled={reloadBusy} onClick={handleReload}>
              {reloadBusy ? "読込中…" : "再読込"}
            </button>
          )}
        </div>
      )}

      <div className="bid-lastrun">
        {last_run ? (
          <>
            最終実行 <b>{formatHistoryAt(last_run.checked_at)}</b>（{RUN_STATUS_LABEL[last_run.status] ?? last_run.status}） ・
            計{last_run.summary.total}件 上げ{last_run.summary.up} / 下げ{last_run.summary.down} / 戻し
            {last_run.summary.revert} / 手動{last_run.summary.manual ?? 0} / 探索{last_run.summary.probe ?? 0} / 据置
            {last_run.summary.hold} / 対象外{last_run.summary.skip}
          </>
        ) : (
          "実行履歴なし（ジョブ稼働後に表示されます）"
        )}
      </div>

      <div className="bid-settings-row">
        <ConfigNumberField
          label="上げ幅（円）"
          value={config.step_up}
          saving={savingKeys.has("config:step_up")}
          onSave={(raw) => handleConfigNumberBlur("step_up", raw)}
        />
        <ConfigNumberField
          label="下げ幅（円）"
          value={config.step_down}
          saving={savingKeys.has("config:step_down")}
          onSave={(raw) => handleConfigNumberBlur("step_down", raw)}
        />
        <ConfigNumberField
          label="床の有効期間（時間）"
          value={config.floor_hours}
          saving={savingKeys.has("config:floor_hours")}
          onSave={(raw) => handleConfigNumberBlur("floor_hours", raw)}
        />
        <label className="bid-setting-checkbox">
          <input
            type="checkbox"
            checked={config.all_missing_alert}
            disabled={savingKeys.has("config:all_missing_alert")}
            onChange={(e) => handleConfigBooleanChange("all_missing_alert", e.target.checked)}
          />
          全キーワード表示なしで通知
        </label>
        <label
          className="bid-setting-checkbox"
          title="RMSの目安CPC・範囲を目標順位のアンカーにして、一気にその付近へジャンプします"
        >
          <input
            type="checkbox"
            checked={config.guide_jump}
            disabled={savingKeys.has("config:guide_jump")}
            onChange={(e) => handleConfigBooleanChange("guide_jump", e.target.checked)}
          />
          目安連動
        </label>
        <ConfigNumberField
          label="上乗せ（円）"
          value={config.guide_margin}
          saving={savingKeys.has("config:guide_margin")}
          onSave={(raw) => handleConfigNumberBlur("guide_margin", raw)}
        />
        <ConfigNumberField
          label="失敗記憶（時間）"
          value={config.fail_memory_hours}
          saving={savingKeys.has("config:fail_memory_hours")}
          onSave={(raw) => handleConfigNumberBlur("fail_memory_hours", raw)}
        />
        <ConfigNumberField
          label="RMS手動変更ロック（時間）"
          value={config.rms_manual_lock_hours}
          saving={savingKeys.has("config:rms_manual_lock_hours")}
          onSave={(raw) => handleConfigNumberBlur("rms_manual_lock_hours", raw)}
        />
        <ConfigNumberField
          label="探索の刻み（%）"
          value={config.probe_step_pct}
          saving={savingKeys.has("config:probe_step_pct")}
          onSave={(raw) => handleConfigNumberBlur("probe_step_pct", raw)}
        />
        <ConfigNumberField
          label="探索の最大回数"
          value={config.probe_max_runs}
          saving={savingKeys.has("config:probe_max_runs")}
          onSave={(raw) => handleConfigNumberBlur("probe_max_runs", raw)}
        />
      </div>

      {isEmpty && manageNumbers.length === 0 && (
        <div className="empty-state">
          順位レポート未取得（ジョブ稼働後に表示されます）
          <div className="bid-add-product-row">
            <input
              type="text"
              placeholder="商品管理番号（例: stq-set）"
              value={newManageNumber}
              onChange={(e) => setNewManageNumber(e.target.value)}
            />
            <button className="tab-btn" onClick={handleAddProduct}>
              追加
            </button>
          </div>
        </div>
      )}

      {manageNumbers.length > 0 && (
        <>
          {isEmpty && <div className="empty-state">順位レポート未取得（ジョブ稼働後に表示されます）</div>}
          <div className="table-wrap bid-table-wrap">
            <table className="data-table bid-table">
              <thead>
                <tr>
                  <th>キーワード</th>
                  <th>順位</th>
                  <th>現CPC</th>
                  <th title="RMS登録済みキーワード表の目安CPC（下に目安範囲）">目安</th>
                  <th title="RPP効果レポートの実績CPC。上=直近日の実効CPC（直近2スナップショットの差分 費用÷クリック）、下=月累計（月初〜前日）。上限CPCの効き目は直近日で見る">
                    実績CPC
                    <span className="bid-cell-sub"> 直近日 / 月累計</span>
                  </th>
                  <th>目標</th>
                  <th>上限CPC</th>
                  <th>前回</th>
                  <th>状態</th>
                </tr>
              </thead>
              <tbody>
                {manageNumbers.map((mn) => {
                  const productRule = rulesByKey.get(ruleKey(mn, ""));
                  const rows = keywordsByManage.get(mn) ?? [];
                  return (
                    <FragmentGroup key={mn} manageNumber={mn}>
                      <tr className="bid-row-product">
                        <td className="bid-keyword-cell">商品既定</td>
                        <td>—</td>
                        <td>—</td>
                        <td>—</td>
                        <td>—</td>
                        <td>
                          <select
                            value={productRule ? String(productRule.target_rank) : ""}
                            disabled={savingKeys.has(ruleKey(mn, ""))}
                            onChange={(e) =>
                              handleTargetSelect(mn, "", e.target.value === "" ? "" : e.target.value, productRule, undefined)
                            }
                          >
                            <option value="">—</option>
                            <option value="1">1位</option>
                            <option value="3">3位以内</option>
                            <option value="5">5位以内</option>
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            placeholder="上限CPC"
                            min={RPP_MIN_CPC}
                            defaultValue={productRule?.max_cpc ?? ""}
                            key={`${mn}-product-maxcpc-${productRule?.max_cpc ?? "none"}`}
                            disabled={savingKeys.has(ruleKey(mn, ""))}
                            onBlur={(e) => handleMaxCpcBlur(mn, "", e.target.value, productRule, undefined)}
                          />
                        </td>
                        <td>—</td>
                        <td>—</td>
                      </tr>
                      {rows.map((row) => {
                        const existing = rulesByKey.get(ruleKey(mn, row.keyword));
                        const savingKey = ruleKey(mn, row.keyword);
                        const selectValue = existing ? (existing.enabled ? String(existing.target_rank) : "off") : "";
                        const effectiveRule = existing ?? productRule;
                        const open = expanded.has(savingKey);
                        return (
                          <Fragment key={row.keyword}>
                            <tr
                              className={`bid-row-clickable${open ? " bid-row-open" : ""}`}
                              onClick={(e) => {
                                // 行内の操作部品（select/input/button/label）はクリックで開閉しない。
                                if ((e.target as HTMLElement).closest("select, input, button, a, label")) return;
                                toggleExpanded(savingKey);
                              }}
                            >
                              <td className="bid-keyword-cell">
                                <button
                                  type="button"
                                  className={`bid-expand-btn${open ? " open" : ""}`}
                                  aria-expanded={open}
                                  aria-label={`${row.keyword}の詳細を${open ? "閉じる" : "開く"}`}
                                  onClick={() => toggleExpanded(savingKey)}
                                >
                                  ▸
                                </button>
                                {row.keyword}
                              </td>
                              <td>{renderRank(row)}</td>
                              <td>{row.cpc !== null ? `¥${row.cpc}` : "—"}</td>
                              <td>
                                <GuideCell guide={row.guide ?? null} />
                              </td>
                              <td>
                                <PerfCell perf={row.perf ?? null} />
                              </td>
                              <td>
                                <select
                                  value={selectValue}
                                  disabled={savingKeys.has(savingKey)}
                                  onChange={(e) => handleTargetSelect(mn, row.keyword, e.target.value, existing, productRule)}
                                >
                                  <option value="">商品既定</option>
                                  <option value="1">1位</option>
                                  <option value="3">3位以内</option>
                                  <option value="5">5位以内</option>
                                  <option value="off">対象外</option>
                                </select>
                              </td>
                              <td>
                                <input
                                  type="number"
                                  placeholder={productRule ? String(productRule.max_cpc) : "—"}
                                  defaultValue={existing?.max_cpc ?? ""}
                                  key={`${mn}-${row.keyword}-maxcpc-${existing?.max_cpc ?? "none"}`}
                                  disabled={savingKeys.has(savingKey)}
                                  onBlur={(e) => handleMaxCpcBlur(mn, row.keyword, e.target.value, existing, productRule)}
                                />
                              </td>
                              <td>
                                <ActionBadge action={row.action} cpc={row.cpc} newCpc={row.new_cpc} />
                              </td>
                              <td className="bid-status-cell">
                                <StatusCell row={row} />
                              </td>
                            </tr>
                            {open && (
                              <tr className="bid-detail-row">
                                <td colSpan={TABLE_COLS}>
                                  <KeywordDetail
                                    row={row}
                                    targetRank={effectiveRule && effectiveRule.enabled ? effectiveRule.target_rank : null}
                                    ruleActive={!!effectiveRule && effectiveRule.enabled}
                                    manualSaving={savingKeys.has(`manual:${savingKey}`)}
                                    probeSaving={savingKeys.has(`probe:${savingKey}`)}
                                    nextRun={nextRun}
                                    onApplyManual={(cpc, lockHours) => handleManualApply(row, cpc, lockHours)}
                                    onCancelManual={() => handleManualCancel(row)}
                                    onStartProbe={() => handleProbeStart(row)}
                                    onCancelProbe={() => handleProbeCancel(row)}
                                  />
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </FragmentGroup>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="bid-add-product-row">
            <input
              type="text"
              placeholder="商品管理番号を追加"
              value={newManageNumber}
              onChange={(e) => setNewManageNumber(e.target.value)}
            />
            <button className="tab-btn" onClick={handleAddProduct}>
              追加
            </button>
          </div>
        </>
      )}

      <button className="bid-log-toggle" onClick={() => setShowLog(!showLog)}>
        {showLog ? "直近のログを閉じる" : `直近のログ（${recent_log.length}件）`}
      </button>
      {showLog && <LogList log={recent_log} />}
    </div>
  );
}

/** 設定行の数値フィールド（onBlur保存。保存後に config 値が変わると key で再マウントして表示を同期）。 */
function ConfigNumberField({
  label,
  value,
  saving,
  onSave,
}: {
  label: string;
  value: number;
  saving: boolean;
  onSave: (raw: string) => void;
}) {
  return (
    <div className="bid-setting-field">
      <label>{label}</label>
      <input
        type="number"
        defaultValue={value}
        key={`${label}-${value}`}
        disabled={saving}
        onBlur={(e) => onSave(e.target.value)}
      />
    </div>
  );
}

/** manage_number見出し行＋商品既定行＋キーワード行をtbody内でまとめて出すための薄いラッパー。 */
function FragmentGroup({ manageNumber, children }: { manageNumber: string; children: React.ReactNode }) {
  return (
    <>
      <tr>
        <td colSpan={TABLE_COLS} className="bid-group-title">
          {manageNumber}
        </td>
      </tr>
      {children}
    </>
  );
}

interface KeywordDetailProps {
  row: RppBidKeywordRow;
  /** 有効な目標順位（キーワード→商品既定の順で解決。対象外/ルール無しは null）。 */
  targetRank: 1 | 3 | 5 | null;
  ruleActive: boolean;
  manualSaving: boolean;
  probeSaving: boolean;
  nextRun: string | null;
  onApplyManual: (cpc: number, lockHours: number | null) => void;
  onCancelManual: () => void;
  onStartProbe: () => void;
  onCancelProbe: () => void;
}

/** 行クリックで開く詳細パネル: 順位ラダー／境界／直近観測／手動CPC／探索。 */
function KeywordDetail({
  row,
  targetRank,
  ruleActive,
  manualSaving,
  probeSaving,
  nextRun,
  onApplyManual,
  onCancelManual,
  onStartProbe,
  onCancelProbe,
}: KeywordDetailProps) {
  const [cpcInput, setCpcInput] = useState<string>(
    row.manual ? String(row.manual.cpc) : row.cpc !== null ? String(row.cpc) : ""
  );
  const [lockInput, setLockInput] = useState<string>(
    row.manual && row.manual.lock_hours !== null ? String(row.manual.lock_hours) : ""
  );

  const cpcNum = cpcInput.trim() === "" ? null : Number(cpcInput);
  const cpcValid =
    cpcNum !== null && Number.isInteger(cpcNum) && cpcNum >= RPP_MIN_CPC && cpcNum <= RPP_MAX_MANUAL_CPC;
  const showCpcError = cpcInput.trim() !== "" && !cpcValid;

  const ladder = [1, 2, 3, 4, 5].map((r) => ({ rank: r, cpc: row.ladder?.find((l) => l.rank === r)?.cpc ?? null }));
  const history = (row.history ?? []).slice(0, 16);
  const probe = row.probe;
  const probeRunning = probe?.status === "running";
  const manual = row.manual;

  return (
    <div className="bid-detail">
      <div className="bid-detail-section">
        <div className="bid-detail-label">順位ラダー（直近7日の観測・その順位以内に入れた最小CPC）</div>
        <div className="bid-ladder">
          {ladder.map((l, i) => (
            <Fragment key={l.rank}>
              {i > 0 && <span className="bid-ladder-sep">/</span>}
              <span className="bid-ladder-item">
                <b>{l.rank}位</b> {l.cpc !== null ? `¥${l.cpc}` : "—"}
              </span>
            </Fragment>
          ))}
        </div>
        <div className="bid-detail-note">
          目安: {row.guide && row.guide.cpc !== null ? `¥${row.guide.cpc}` : "—"}
          {row.guide && row.guide.min !== null && row.guide.max !== null
            ? `（範囲 ${row.guide.min}–${row.guide.max}）`
            : ""}
        </div>
      </div>

      <div className="bid-detail-section">
        <div className="bid-detail-label">境界</div>
        <div className="bid-detail-text">{boundsText(row.bounds ?? null)}</div>
      </div>

      <div className="bid-detail-section">
        <div className="bid-detail-label">直近観測（新しい順・最大16）</div>
        {history.length === 0 ? (
          <div className="bid-detail-text">—</div>
        ) : (
          <div className="bid-hist">
            {history.map((h, i) => {
              const cls =
                targetRank !== null && h.rank !== null ? (h.rank <= targetRank ? " bid-hist-ok" : " bid-hist-ng") : "";
              return (
                <span key={`${h.at}-${i}`} className={`bid-hist-chip${cls}`} title={formatHistoryAt(h.at)}>
                  {h.cpc !== null ? `¥${h.cpc}` : "—"}→{h.rank !== null ? `${h.rank}位` : "—"}
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div className="bid-detail-section">
        <div className="bid-detail-label">手動CPC</div>
        <div className="bid-manual-form">
          <input
            type="number"
            min={RPP_MIN_CPC}
            max={RPP_MAX_MANUAL_CPC}
            step={1}
            inputMode="numeric"
            placeholder={`${RPP_MIN_CPC}以上`}
            value={cpcInput}
            disabled={manualSaving}
            onChange={(e) => setCpcInput(e.target.value)}
            aria-label="手動CPC（円）"
          />
          <select value={lockInput} disabled={manualSaving} onChange={(e) => setLockInput(e.target.value)} aria-label="ロック">
            {LOCK_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={manualSaving || !cpcValid}
            onClick={() => cpcNum !== null && onApplyManual(cpcNum, lockInput === "" ? null : Number(lockInput))}
          >
            反映
          </button>
          {manual && (
            <button type="button" className="btn btn-sm" disabled={manualSaving} onClick={onCancelManual}>
              解除
            </button>
          )}
        </div>
        {showCpcError && (
          <div className="bid-detail-error">
            {RPP_MIN_CPC}〜{RPP_MAX_MANUAL_CPC}円の整数で入力してください。
          </div>
        )}
        {manual && <div className="bid-detail-text">{manualSummary(manual)}</div>}
        <div className="bid-detail-note">
          反映は次回run{nextRun ? `（目安 ${nextRun}）` : "（3時間ごと）"}。上限CPCには縛られません（下限{RPP_MIN_CPC}円）。
        </div>
      </div>

      <div className="bid-detail-section">
        <div className="bid-detail-label">探索</div>
        {probeRunning && probe ? (
          <>
            <div className="bid-detail-text">
              探索中 <b>{probe.runs}/{probe.max_runs}</b> 回（起動 {formatHistoryAt(probe.started_at)}
              {probe.restore_cpc !== null ? `・元のCPC ¥${probe.restore_cpc}` : ""}・{probeStartText(probe)}・
              {probe.step_pct}%刻み・{probeEndText(probe, targetRank)}・下限¥{probe.min_cpc}）
            </div>
            <div className="bid-manual-form">
              <button type="button" className="btn btn-sm btn-danger" disabled={probeSaving} onClick={onCancelProbe}>
                中止
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="bid-manual-form">
              <button
                type="button"
                className="btn btn-sm"
                disabled={probeSaving || row.cpc === null || !ruleActive}
                onClick={onStartProbe}
              >
                探索を開始
              </button>
            </div>
            <div className="bid-detail-note">
              意図的にCPCを段階的に下げて順位を落とし、1〜5位の単価を調べます（探索中のCPCは目標を満たしません）。
            </div>
            <div className="bid-detail-note">
              終了条件: 1位狙いは1位を外した時点（目安範囲の上限＋上乗せから探り始め、見つかった最安の1位CPCに戻す）／3・5位狙いは5位より下に落ちた時点。
              {targetRank === 1
                ? " このキーワードは1位狙いです。"
                : targetRank !== null
                  ? ` このキーワードは${targetRank}位以内狙いです。`
                  : ""}
            </div>
            {!ruleActive && <div className="bid-detail-note">目標が有効なキーワードのみ探索できます（対象外・ルール無しは不可）。</div>}
            {row.lock_active && (
              <div className="bid-detail-note">ロック中は探索が進みません。先に手動CPCの「解除」をしてください。</div>
            )}
            {probe && probe.status !== "running" && (
              <div className="bid-detail-text">
                前回の探索: {probe.status === "done" ? "完了" : "中止"}
                {probe.finished_at ? ` ${formatHistoryAt(probe.finished_at)}` : ""}（{probe.runs}回・{probeStartText(probe)}）
              </div>
            )}
          </>
        )}
        {probe && probe.log.length > 0 && (
          <div className="bid-probe-log">
            {probe.log.map((l, i) => (
              <span key={`${l.at}-${i}`} className="bid-hist-chip" title={formatHistoryAt(l.at)}>
                {formatHistoryAt(l.at)} ¥{l.cpc}→{l.rank !== null ? `${l.rank}位` : "表示なし"}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LogList({ log }: { log: RppBidLogRow[] }) {
  if (log.length === 0) {
    return <div className="empty-state">ログがありません。</div>;
  }
  return (
    <div className="bid-log-list">
      {log.map((row) => (
        <div className="bid-log-row" key={row.id}>
          <span className="bid-log-time">{formatHistoryAt(row.checked_at)}</span>
          <span>{row.keyword || "商品既定"}</span>
          <span>
            {row.rank !== null ? `${row.rank}位` : "表示なし"} / ¥{row.cpc ?? "—"}
            {row.new_cpc !== null ? ` → ¥${row.new_cpc}` : ""}
          </span>
          <span>{ACTION_LABEL[row.action]({ cpc: row.cpc, new_cpc: row.new_cpc })}</span>
          <span>{RUN_STATUS_LABEL[row.status] ?? row.status}</span>
        </div>
      ))}
    </div>
  );
}
